import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Moon, Eye, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STATUS = { sent: ["Accettata dal provider", "text-emerald-300"], error: ["Errore", "text-rose-300"],
  deferred: ["Rinviata (limite invii)", "text-amber-300"], pending: ["In invio", "text-slate-400"] };
const TYPE = { admin: "Amministratore", driver: "Autista", supplier: "Fornitore" };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export function DailyDigestCard() {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = async () => {
    try { setData((await api.get("/daily-digest/log")).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (v) => {
    try { await api.put("/daily-digest/settings", { enabled: v }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const run = async (force) => {
    const q = force ? "Reinviare a tutti i destinatari il riepilogo di domani, anche se già inviato?"
      : "Inviare adesso il riepilogo di domani? L'invio automatico delle 21:00 salterà i destinatari già serviti.";
    if (!window.confirm(q)) return;
    try {
      await api.post("/daily-digest/run", { force });
      toast.success(force ? "Reinvio avviato" : "Invio avviato (i destinatari già serviti vengono saltati)");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openPreview = async () => {
    try { setPreview((await api.get("/daily-digest/preview")).data); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 space-y-4" data-testid="daily-digest-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-white font-semibold"><Moon size={16} className="text-amber-400" />Promemoria serale 21:00</div>
          <div className="text-xs text-slate-400 mt-0.5">Un'unica email con i servizi di domani all'amministratore, a ogni autista e a ogni fornitore.</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={data.settings.enabled} onCheckedChange={toggle} data-testid="daily-digest-enabled" />Attivo</label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={openPreview} data-testid="daily-digest-preview-btn" className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:border-amber-400"><Eye size={13} />Anteprima di domani</button>
        <button type="button" onClick={() => run(false)} data-testid="daily-digest-run-btn" className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs border border-amber-500/40 text-amber-200"><Send size={13} />Invia ora</button>
        <button type="button" onClick={() => run(true)} data-testid="daily-digest-force-btn" className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs border border-rose-500/40 text-rose-200"><RotateCcw size={13} />Reinvia a tutti</button>
      </div>
      <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="daily-digest-log">
        {data.sends.length === 0 && <div className="text-xs text-slate-500">Nessun invio registrato.</div>}
        {data.sends.map(s => (
          <div key={s.id} className="flex flex-wrap justify-between gap-2 text-xs border-b border-slate-800/70 pb-1.5" data-testid={`daily-digest-send-${s.id}`}>
            <span className="text-slate-300">{s.target_date.split("-").reverse().join("/")} · {TYPE[s.recipient_type]} · {s.email} · {s.services_count} serviz{s.services_count === 1 ? "io" : "i"}{s.manual ? " · manuale" : ""}</span>
            <span className={(STATUS[s.status] || STATUS.pending)[1]} title={s.error || ""}>{(STATUS[s.status] || STATUS.pending)[0]} · {fmt(s.sent_at || s.created_at)}</span>
          </div>
        ))}
      </div>
      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent className="max-w-2xl bg-[#0C0B09] border-amber-500/30" data-testid="daily-digest-preview-dialog">
            <DialogHeader><DialogTitle className="text-white">Anteprima riepilogo {preview.date.split("-").reverse().join("/")}</DialogTitle></DialogHeader>
            <div className="text-xs text-slate-400 space-y-1">
              <div>Amministratore: {preview.admin_email || "non configurato"}</div>
              {preview.recipients.map(r => <div key={r.id}>{TYPE[r.type]} {r.name} ({r.email}): {r.services.length} servizi</div>)}
              {preview.issues.map((i, k) => <div key={k} className="text-rose-300">{i}</div>)}
            </div>
            <iframe title="anteprima" srcDoc={preview.admin_html} className="w-full h-[55vh] rounded-lg border border-slate-800 bg-black" data-testid="daily-digest-preview-frame" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
